import React, { useEffect, useMemo, useRef, useState } from "react";
import useCheckoutStore from "../../store/useCheckoutStore";
import useShopStore from "../../store/useShopStore";
import useUserAddressStore from "../../store/useUserAddressStore";

const PL_POSTCODE = /^\d{2}-\d{3}$/;

const normalizePostcode = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2, 5)}`;
};

const validate = (addr, requireAll = true) => {
  const errors = {};
  if (requireAll && !addr.street?.trim()) errors.street = "Ulica i numer są wymagane";
  if (requireAll && !addr.city?.trim()) errors.city = "Miasto jest wymagane";
  if (requireAll && !PL_POSTCODE.test(addr.postcode || "")) errors.postcode = "Kod: NN-NNN";
  return { valid: Object.keys(errors).length === 0, errors };
};

const DeliveryAddressForm = ({
  onAddressChange,
  onValidityChange,
  requireAll = true,
}) => {
  const { checkoutData } = useCheckoutStore();
  const shop = useShopStore((s) => s.shop);

  const streetS = useUserAddressStore((s) => s.street);
  const houseNumberS = useUserAddressStore((s) => s.houseNumber);
  const cityS = useUserAddressStore((s) => s.city);
  const postalS = useUserAddressStore((s) => s.postalCode);
  const coordsS = useUserAddressStore((s) => s.coords);
  const pickedFromMap = useUserAddressStore((s) => s.pickedFromMap);

  const userEditingRef = useRef(false);

  // ⬇️ Smart fallback from Zustand + checkoutData
  const [addr, setAddr] = useState(() => {
    const combinedStreet = [streetS, houseNumberS].filter(Boolean).join(" ").trim();
    return {
      street: checkoutData.address?.street || combinedStreet || "",
      city: checkoutData.address?.city || cityS || "",
      postcode:
        checkoutData.address?.postcode || (postalS ? normalizePostcode(postalS) : "") || "",
      flat: checkoutData.address?.flat || "",
      floor: checkoutData.address?.floor || "",
      accessCode: checkoutData.address?.accessCode || "",
      lat: checkoutData.address?.lat || coordsS?.lat || "",
      lng: checkoutData.address?.lng || coordsS?.lng || "",
    };
  });

  // ⬇️ Auto-sync from picked map (unless user is already editing)
  useEffect(() => {
    if (!pickedFromMap || userEditingRef.current) return;

    const streetCombined = [streetS, houseNumberS].filter(Boolean).join(" ").trim();

    const next = {
      ...addr,
      street: streetCombined || addr.street,
      city: cityS || shop?.storeAddress?.city || addr.city,
      postcode: postalS ? normalizePostcode(postalS) : addr.postcode,
      lat: coordsS?.lat ?? addr.lat,
      lng: coordsS?.lng ?? addr.lng,
    };

    setAddr(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedFromMap, streetS, houseNumberS, cityS, postalS, coordsS, shop?.storeAddress?.city]);

  // ⬇️ Validate on every change
  const { valid, errors } = useMemo(() => validate(addr, requireAll), [addr, requireAll]);

  useEffect(() => {
    onValidityChange?.(valid, errors);
    onAddressChange?.(addr);
  }, [addr, valid, errors, onAddressChange, onValidityChange]);

  const edit = (patch) => {
    userEditingRef.current = true;
    setAddr((prev) => ({ ...prev, ...patch }));
  };

  return (
    <div className="card">
      <h2>Adres dostawy</h2>

      <input
        type="text"
        placeholder="Ulica i numer"
        value={addr.street}
        onChange={(e) => edit({ street: e.target.value })}
        required={requireAll}
        aria-invalid={!!errors.street}
      />
      {errors.street && <small className="error">{errors.street}</small>}

      <input
        type="text"
        placeholder="Miasto"
        value={addr.city}
        onChange={(e) => edit({ city: e.target.value })}
        required={requireAll}
        aria-invalid={!!errors.city}
      />
      {errors.city && <small className="error">{errors.city}</small>}

      <div className="input-row">
        <input
          type="text"
          className="small-input"
          placeholder="Kod pocztowy"
          value={addr.postcode}
          onChange={(e) => edit({ postcode: normalizePostcode(e.target.value) })}
          required={requireAll}
          aria-invalid={!!errors.postcode}
        />
        <input
          type="text"
          className="small-input"
          placeholder="Nr mieszkania"
          value={addr.flat}
          onChange={(e) => edit({ flat: e.target.value })}
        />
        <input
          type="text"
          className="small-input"
          placeholder="Piętro"
          value={addr.floor}
          onChange={(e) => edit({ floor: e.target.value })}
        />
      </div>
      {errors.postcode && <small className="error">{errors.postcode}</small>}

      <input
        type="text"
        placeholder="Kod dostępu"
        value={addr.accessCode}
        onChange={(e) => edit({ accessCode: e.target.value })}
      />
    </div>
  );
};

export default DeliveryAddressForm;
